// Public API do client REST. Endpoints concretos por feature em `features/<feat>/api/`.
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithRefresh } from './baseQuery';

// Base "vazia" — features injetam endpoints via `api.injectEndpoints({...})`.
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRefresh,
  tagTypes: [
    'User',
    'Contact',
    'Conversation',
    'ConversationCost',
    'Message',
    // Respostas rápidas + variáveis da loja (id `STORE:<storeId>`). O WS
    // `quick_replies.changed` invalida a tag da loja e o menu "/" recarrega sozinho.
    'QuickReply',
    // Figurinhas recentes da loja (id = storeId), para o seletor do composer.
    'Sticker',
    // Seletor de ENVIO do inbox (aprovados de uma conversa/loja).
    'Template',
    // Gestão de templates da EMPRESA (qualquer status). Tag separada de 'Template'
    // porque as duas listas têm escopo e ciclo diferentes: o seletor é por loja e só
    // aprovados; esta é da empresa e inclui pendente/rejeitado. As mutations daqui
    // invalidam as DUAS — criar ou apagar um template muda o que o inbox pode enviar.
    'WhatsappTemplate',
    'Quote',
    'Order',
    'Product',
    'Store',
    'StoreMember',
    'StorePrice',
    'StoreCatalog',
    'StoreInvite',
    // Números de WhatsApp da loja (N por loja). Tag própria e não `Store` porque a
    // lista muda por ações que não tocam a loja (trocar o principal, desconectar),
    // e invalidar `Store` recarregaria a aba inteira a cada clique.
    'StoreWhatsappNumber',
    // Importação de histórico de Coexistence. Tag PRÓPRIA e não 'StoreWhatsappNumber':
    // o progresso muda a cada ~15s enquanto a importação corre, e reaproveitar a tag do
    // número faria a lista inteira de números refazer a busca junto.
    'HistoryImport',
    // RF-03 — benefícios por categoria do cliente. Escopo de EMPRESA (não de loja),
    // por isso tag própria: editar num lugar tem que invalidar a lista inteira.
    'TierBenefit',
    'Leaderboard',
    'Analysis',
    'CrmClient',
    'CrmFollowUp',
    'CrmReativar',
    'CrmCampaign',
    'CrmBoard',
    'RealtimeCounts',
    'Metrics',
    'SalesHistory',
    'Goal',
    // PDV / Guichê (RF-01..RF-08). Separado de 'Order' (orçamento→pedido do chat):
    // a fila e o histórico do balcão invalidam sozinhos.
    'PdvOrder',
    'PdvSummary',
    'PdvCatalog',
    // Quadro de produção (RF-04). Tag própria e não 'PdvOrder': as duas telas leem a
    // MESMA tabela por recortes diferentes, e mover um card na produção precisa
    // invalidar a fila do guichê também (o pedido muda de status lá) — quem faz essa
    // ponte são os `invalidatesTags` das mutations, não uma tag compartilhada.
    'ProductionOrder',
    // Favoritos do catálogo. Tag SEPARADA de 'PdvCatalog' de propósito: a busca do
    // guichê acumula páginas via `merge`, e invalidá-la refaria a query com o
    // cursor da ÚLTIMA página, concatenando essa página em cima de si mesma.
    // Favoritar só pode invalidar a lista de favoritos.
    'PdvCatalogFavorite',

    // ── Indicadores de atendimento (MET-01..MET-28) ───────────────────────────
    // NÃO reusar a tag 'Metrics' acima: ela é invalidada por mutation de venda/PDV,
    // e o módulo inteiro (35+ endpoints) recarregaria a cada venda concluída.
    // Uma tag por natureza de dado, porque os TTLs e os invalidadores diferem:
    // agregado (minutos) × tempo real (10s) × configuração (só muda por escrita).
    'MetricsAggregate',
    'MetricsRealtime',
    'MetricsDrilldown',
    'MetricsSlaConfig',
    'MetricsLossReason',
    'MetricsStage',
    'MetricsOrigin',
    'MetricsThreshold',
    'MetricsBusinessHours',
    'MetricsSurveyConfig',
    'MetricsWaHealth',
    'MetricsTemplateStats',
    'MetricsExportJob',
  ],
  endpoints: () => ({}),
});

export { baseQueryWithRefresh } from './baseQuery';
export type { paths, components, operations } from './generated';
